import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  Users, 
  Plus, 
  MessageSquare, 
  ArrowUp,
  Clock,
  User,
  Loader2,
  Send
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Forum() {
  const { user } = useAuth();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('all');
  const [newPostOpen, setNewPostOpen] = useState(false);
  const [newPost, setNewPost] = useState({ title: '', content: '', category: '' });
  const [submitting, setSubmitting] = useState(false);
  const [selectedPost, setSelectedPost] = useState(null);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [commentsLoading, setCommentsLoading] = useState(false);

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    try {
      const response = await axios.get(`${API}/forum/posts`);
      setPosts(response.data);
    } catch (error) {
      console.error('Failed to fetch posts:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchComments = async (postId) => {
    setCommentsLoading(true);
    try {
      const response = await axios.get(`${API}/forum/posts/${postId}/comments`);
      setComments(response.data);
    } catch (error) {
      console.error('Failed to fetch comments:', error);
    } finally {
      setCommentsLoading(false);
    }
  };

  const handleCreatePost = async () => {
    if (!user) {
      toast.error('Please login to create a post');
      return;
    }
    if (!newPost.title || !newPost.content || !newPost.category) {
      toast.error('Please fill in all fields');
      return;
    }

    setSubmitting(true);
    try {
      const response = await axios.post(`${API}/forum/posts`, newPost);
      setPosts([response.data, ...posts]);
      setNewPost({ title: '', content: '', category: '' });
      setNewPostOpen(false);
      toast.success('Post created successfully!');
    } catch (error) {
      toast.error('Failed to create post');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpvote = async (postId) => {
    if (!user) {
      toast.error('Please login to upvote');
      return;
    }
    try {
      await axios.post(`${API}/forum/posts/${postId}/upvote`);
      setPosts(posts.map(p => 
        p.id === postId ? { ...p, upvotes: p.upvotes + 1 } : p
      ));
    } catch (error) {
      toast.error('Failed to upvote');
    }
  };

  const handleAddComment = async () => {
    if (!user) {
      toast.error('Please login to comment');
      return;
    }
    if (!newComment.trim()) return;

    try {
      const response = await axios.post(`${API}/forum/posts/${selectedPost.id}/comments`, {
        content: newComment
      });
      setComments([...comments, response.data]);
      setNewComment('');
      setPosts(posts.map(p => 
        p.id === selectedPost.id ? { ...p, comments_count: p.comments_count + 1 } : p
      ));
    } catch (error) {
      toast.error('Failed to add comment');
    }
  };

  const openPostDetails = (post) => {
    setSelectedPost(post);
    fetchComments(post.id);
  };

  const categories = [
    { id: 'all', label: 'All Topics' },
    { id: 'general', label: 'General' },
    { id: 'help', label: 'Help & Support' },
    { id: 'tools', label: 'Tools & Apps' },
    { id: 'news', label: 'News Discussion' },
    { id: 'legal', label: 'Legal & Rights' },
  ];

  const filteredPosts = activeCategory === 'all' 
    ? posts 
    : posts.filter(p => p.category === activeCategory);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    
    if (diffHours < 1) return 'Just now';
    if (diffHours < 24) return `${diffHours}h ago`;
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#00FF94] animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] py-8" data-testid="forum-page">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-lg bg-[#00FF94]/10 border border-[#00FF94]/20 flex items-center justify-center">
              <Users className="w-6 h-6 text-[#00FF94]" />
            </div>
            <div>
              <h1 className="font-heading font-bold text-3xl text-white">
                Community Forum
              </h1>
              <p className="text-gray-500">
                Discuss privacy topics with the community
              </p>
            </div>
          </div>

          <Dialog open={newPostOpen} onOpenChange={setNewPostOpen}>
            <DialogTrigger asChild>
              <Button className="btn-primary" data-testid="new-post-btn">
                <Plus className="w-4 h-4 mr-2" />
                New Post
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#0a0a0a] border-[#262626]">
              <DialogHeader>
                <DialogTitle className="text-white">Create New Post</DialogTitle>
                <DialogDescription className="text-gray-500">
                  Share your thoughts with the community
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div>
                  <label className="text-sm text-gray-400 mb-2 block">Title</label>
                  <Input
                    value={newPost.title}
                    onChange={(e) => setNewPost({ ...newPost, title: e.target.value })}
                    placeholder="Enter post title"
                    className="bg-[#171717] border-[#262626] text-white"
                    data-testid="new-post-title"
                  />
                </div>
                <div>
                  <label className="text-sm text-gray-400 mb-2 block">Category</label>
                  <Select
                    value={newPost.category}
                    onValueChange={(value) => setNewPost({ ...newPost, category: value })}
                  >
                    <SelectTrigger className="bg-[#171717] border-[#262626] text-white" data-testid="new-post-category">
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0a0a0a] border-[#262626]">
                      {categories.slice(1).map((cat) => (
                        <SelectItem key={cat.id} value={cat.id}>{cat.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm text-gray-400 mb-2 block">Content</label>
                  <Textarea
                    value={newPost.content}
                    onChange={(e) => setNewPost({ ...newPost, content: e.target.value })}
                    placeholder="Write your post content..."
                    className="bg-[#171717] border-[#262626] text-white min-h-[120px]"
                    data-testid="new-post-content"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setNewPostOpen(false)} className="border-[#262626] text-gray-400">
                  Cancel
                </Button>
                <Button onClick={handleCreatePost} disabled={submitting} className="btn-primary" data-testid="submit-post-btn">
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Post
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        {/* Category Filter */}
        <div className="flex flex-wrap gap-2 mb-8">
          {categories.map((cat) => (
            <button
              key={cat.id}
              onClick={() => setActiveCategory(cat.id)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                activeCategory === cat.id
                  ? 'bg-[#00FF94] text-black'
                  : 'bg-[#0a0a0a] border border-[#262626] text-gray-400 hover:text-white hover:border-[#00FF94]/30'
              }`}
              data-testid={`forum-category-${cat.id}`}
            >
              {cat.label}
            </button>
          ))}
        </div>

        {/* Posts List */}
        <div className="space-y-4">
          {filteredPosts.length === 0 ? (
            <div className="text-center py-16 bg-[#0a0a0a] border border-[#262626] rounded-xl">
              <Users className="w-12 h-12 text-gray-600 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-400 mb-2">No posts yet</h3>
              <p className="text-gray-500 text-sm mb-4">Be the first to start a discussion!</p>
              {user && (
                <Button onClick={() => setNewPostOpen(true)} className="btn-primary">
                  Create First Post
                </Button>
              )}
            </div>
          ) : (
            filteredPosts.map((post, index) => (
              <Card 
                key={post.id}
                className="bg-[#0a0a0a] border-[#262626] hover:border-[#00FF94]/30 transition-colors cursor-pointer"
                onClick={() => openPostDetails(post)}
                data-testid={`forum-post-${index}`}
              >
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    {/* Upvote */}
                    <div className="flex flex-col items-center gap-1">
                      <button
                        onClick={(e) => { e.stopPropagation(); handleUpvote(post.id); }}
                        className="w-8 h-8 rounded bg-[#171717] border border-[#262626] flex items-center justify-center text-gray-500 hover:text-[#00FF94] hover:border-[#00FF94]/30 transition-colors"
                        data-testid={`upvote-btn-${index}`}
                      >
                        <ArrowUp className="w-4 h-4" />
                      </button>
                      <span className="text-sm font-mono text-gray-400">{post.upvotes}</span>
                    </div>

                    {/* Content */}
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <Badge className="bg-[#00FF94]/10 text-[#00FF94] border-[#00FF94]/20 capitalize text-xs">
                          {post.category}
                        </Badge>
                        <span className="text-xs text-gray-500 flex items-center gap-1">
                          <User className="w-3 h-3" />
                          {post.author_name}
                        </span>
                        <span className="text-xs text-gray-600">•</span>
                        <span className="text-xs text-gray-500 flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {formatDate(post.created_at)}
                        </span>
                      </div>
                      <h3 className="font-heading font-semibold text-white text-lg mb-2">
                        {post.title}
                      </h3>
                      <p className="text-gray-500 text-sm line-clamp-2">
                        {post.content}
                      </p>
                      <div className="flex items-center gap-4 mt-3">
                        <span className="text-xs text-gray-500 flex items-center gap-1">
                          <MessageSquare className="w-3 h-3" />
                          {post.comments_count} comments
                        </span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>

        {/* Post Detail Dialog */}
        <Dialog open={!!selectedPost} onOpenChange={() => setSelectedPost(null)}>
          <DialogContent className="bg-[#0a0a0a] border-[#262626] max-w-2xl max-h-[80vh] overflow-y-auto">
            {selectedPost && (
              <>
                <DialogHeader>
                  <div className="flex items-center gap-2 mb-2">
                    <Badge className="bg-[#00FF94]/10 text-[#00FF94] border-[#00FF94]/20 capitalize text-xs">
                      {selectedPost.category}
                    </Badge>
                    <span className="text-xs text-gray-500">{selectedPost.author_name}</span>
                    <span className="text-xs text-gray-600">•</span>
                    <span className="text-xs text-gray-500">{formatDate(selectedPost.created_at)}</span>
                  </div>
                  <DialogTitle className="text-white text-xl">{selectedPost.title}</DialogTitle>
                </DialogHeader>
                
                <div className="py-4">
                  <p className="text-gray-400 leading-relaxed whitespace-pre-wrap">
                    {selectedPost.content}
                  </p>
                </div>

                <div className="border-t border-[#262626] pt-4">
                  <h4 className="font-medium text-white mb-4">Comments ({comments.length})</h4>
                  
                  {commentsLoading ? (
                    <div className="flex justify-center py-4">
                      <Loader2 className="w-5 h-5 text-[#00FF94] animate-spin" />
                    </div>
                  ) : (
                    <div className="space-y-4 mb-4">
                      {comments.map((comment, index) => (
                        <div key={comment.id} className="bg-[#171717] rounded-lg p-4" data-testid={`comment-${index}`}>
                          <div className="flex items-center gap-2 mb-2">
                            <span className="text-sm text-white font-medium">{comment.author_name}</span>
                            <span className="text-xs text-gray-500">{formatDate(comment.created_at)}</span>
                          </div>
                          <p className="text-gray-400 text-sm">{comment.content}</p>
                        </div>
                      ))}
                      {comments.length === 0 && (
                        <p className="text-gray-500 text-sm text-center py-4">No comments yet. Be the first!</p>
                      )}
                    </div>
                  )}

                  {user && (
                    <div className="flex gap-2">
                      <Input
                        value={newComment}
                        onChange={(e) => setNewComment(e.target.value)}
                        placeholder="Add a comment..."
                        className="bg-[#171717] border-[#262626] text-white"
                        data-testid="comment-input"
                      />
                      <Button onClick={handleAddComment} className="btn-primary" data-testid="submit-comment-btn">
                        <Send className="w-4 h-4" />
                      </Button>
                    </div>
                  )}
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
